import { WebGLUIRenderLayer } from "./core/runtime/webgl_ui_render_layer.js";
import { WebGLUILock } from "./webgl_ui/webgl_ui_lock.js";
import { WebGLUIGlint } from "./webgl_ui/webgl_ui_glint.js";
import { IRenderLayerStats, WebGLTextureSource } from "./core/types/webgl_types.js";
import { WebGLDynamicAtlas, IWebGLDynamicAtlasAllocation } from "./core/rendering/textures/webgl_dynamic_atlas.js";
import { WebGLShelfAllocator } from "./core/rendering/textures/webgl_shelf_allocator.js";

export class WebGLUIRuntime
{
  private static main_layer: WebGLUIRenderLayer;
  private static main_atlas: WebGLDynamicAtlas;

  public static init( main_canvas: HTMLCanvasElement ): void
  {
    this.main_layer = new WebGLUIRenderLayer( main_canvas );
    this.main_atlas = new WebGLDynamicAtlas( new WebGLShelfAllocator( 512, 1 ) );
    this.main_atlas.init( this.main_layer.gl_ctx.gl );
  }

  public static getStats(): IRenderLayerStats
  {
    return this.main_layer.getStats();
  }

  public static setup(): void
  {
    this.main_layer.register( new WebGLUILock( this.main_layer ) );
    this.main_layer.register( new WebGLUIGlint( this.main_layer ) );
  }

  public static update( now: number ): void
  {
    this.main_layer.update( now );
  }

  public static renderMain( dpr: number ): void
  {
    this.main_layer.render( dpr );
  }

  public static hasMainElements(): boolean
  {
    return this.main_layer.hasActiveElements();
  }

  public static resize(): void
  {
    this.main_layer.resize();
  }

  public static allocateInMainAtlas( canvas: WebGLTextureSource, width?: number, height?: number ): IWebGLDynamicAtlasAllocation | null
  {
    return this.main_atlas.allocate( canvas, width, height );
  }

  public static freeInMainAtlas( source: WebGLTextureSource ): void
  {
    this.main_atlas.free( source );
  }

  public static getMainAtlasTexture(): WebGLTexture | null
  {
    return this.main_atlas.getTexture();
  }

  public static getMainAtlasStats()
  {
    if ( !this.main_atlas )
    {
      return {
        active: 0,
        in_use: 0,
        cached: 0,
        layer : 0,
        total : 0,
        last_w: 0,
        last_h: 0,
        shelf_free_w: 0,
        layer_free_h: 0,
        empty_layers: 0,
        size: 0,
      };
    }
    return this.main_atlas.getStats();
  }

  public static clearAll(): void
  {
    this.main_layer.clearAll();
    this.main_atlas.clearAll();
  }

  public static dispose(): void
  {
    this.main_layer.dispose();
    this.main_atlas.dispose();
  }

  public static getUILock(): WebGLUILock
  {
    return this.main_layer.get( WebGLUILock );
  }

  public static getUIGlint(): WebGLUIGlint
  {
    return this.main_layer.get( WebGLUIGlint );
  }
}
